// Multi-factor authentication calls - see docs/extranet-mfa-react-contract.md
//
// Requests are form-encoded (contract preamble); responses are the standard
// extranet envelopes. Every function resolves with the `data` object from a
// successful call and rejects with the raw axios error, so callers can branch
// on `apiErrorCode(error)` / `isUnauthorised(error)`.
//
// Only the pending marker lives in the store (see mfaReducer). The enrolment
// secret, QR image and code-entry state stay in component state on purpose -
// pushing a TOTP secret into Redux would expose it to devtools and any state
// serialisation for no benefit.

import {extranetApi, resolvedApiFailure} from "../../services/Api";
import {loginUser} from "./authActions";
import {MFA_ENDPOINTS} from "../../config/MfaConfig";

const querystring = require('querystring');

const FORM_HEADERS = {
  'Content-Type': 'application/x-www-form-urlencoded'
};

// extranetApi resolves HTTP errors instead of rejecting (see services/Api.js),
// so rethrow them here. That keeps one convention for every caller below:
// resolved means 2xx with the success envelope, rejected means an axios error
// carrying the contract envelope in `error.response.data`.
const unwrapMfaResponse = (response) => {
  const failure = resolvedApiFailure(response);
  if (failure) throw failure;
  return response.data.data;
};

// Percent-decodes a single URL path segment, tolerating values that are already
// decoded. Paired with the `encodeURIComponent` calls below this round-trips
// correctly either way, which matters because the emailed `:verifyCode` is an
// obfuscated TOTP and may contain characters the browser left encoded.
export const decodePathSegment = (segment) => {
  if (typeof segment !== 'string') return '';
  try {
    return decodeURIComponent(segment);
  } catch (e) {
    // Malformed escape sequence - use it verbatim rather than blowing up.
    return segment;
  }
}

/* --------------------------------------------------------------------------
 * Action creators
 * ----------------------------------------------------------------------- */

// Password accepted but a second factor is still required. Only the three
// fields the login response exposed.
export function mfaPending(payload) {
  return {
    type: 'MFA_PENDING',
    payload: payload
  }
}

// Abandon mid-flow, or the server told us the pending session is gone.
export function mfaCleared() {
  return {
    type: 'MFA_CLEAR',
    payload: {}
  }
}

// The account is enrolled although the marker still claims it is not.
// Corrects the stale `mfa_enrolled` flag so the verify screen's mount guard
// lets the user through instead of bouncing back to enrolment.
export function mfaEnrolled() {
  return {
    type: 'MFA_ENROLLED',
    payload: {}
  }
}

// The account is not enrolled (the server just told us so with a 1026), but
// the marker still claims it is. Corrects the stale flag so the enrol
// screen's mount guard lets the user through instead of bouncing straight
// back to verify.
export function mfaUnenrolled() {
  return {
    type: 'MFA_UNENROLLED',
    payload: {}
  }
}

// MFA stage 2 returned the full login payload, so the flow is complete. Shares
// LOGIN_USER so the session cookie + lscache flush stay in one place, and so
// mfaReducer can hook its cleanup onto the same action.
export const completeMfaLogin = (dispatch, payload) => {
  dispatch(loginUser(payload));
};

/* --------------------------------------------------------------------------
 * Calls
 * ----------------------------------------------------------------------- */

// GET profile - user metadata for an authenticated session. Used to hydrate
// the store when a 1023 lands on a screen whose store is still empty (the
// login completed in another tab). 401s for pending-only sessions like every
// other data endpoint (contract section 7) - but a 1023 already guarantees an
// authenticated session, so this is expected to succeed.
//
// Opts out of the global 401 logout+redirect (see services/Api.js): a 401
// here is an expected answer (pending-only session), handled by the caller.
export const fetchUserProfile = () => {
  return extranetApi.get('profile', {skipAuthRedirect: true})
    .then(unwrapMfaResponse);
};

// GET mfa/register - QR + secret for an unenrolled user. Requires a pending
// MFA session (401/1000 without one). The payload is stable for the account, so
// the screen keeps it across failed confirmations instead of re-fetching.
export const fetchMfaRegistration = () => {
  return extranetApi.get(MFA_ENDPOINTS.register)
    .then(unwrapMfaResponse);
};

// POST mfa/register - confirm enrolment with a TOTP code. On success the
// response is the *full login payload*.
export const confirmMfaEnrolment = (totpCode) => {
  return extranetApi.post(
    MFA_ENDPOINTS.register,
    querystring.stringify({totp_code: totpCode}),
    {headers: FORM_HEADERS}
  )
    .then(unwrapMfaResponse);
};

// POST mfa/verify - challenge an already-enrolled user. Same full login payload
// on success.
export const confirmMfaChallenge = (totpCode) => {
  return extranetApi.post(
    MFA_ENDPOINTS.verify,
    querystring.stringify({totp_code: totpCode}),
    {headers: FORM_HEADERS}
  )
    .then(unwrapMfaResponse);
};

// POST mfa/email - send a fresh fallback link. Stores a new token, so it
// implicitly invalidates any previously emailed link. Requires a pending MFA
// session.
export const sendMfaEmailLink = () => {
  return extranetApi.post(MFA_ENDPOINTS.email, '', {headers: FORM_HEADERS})
    .then(unwrapMfaResponse);
};

// GET mfa/email-verify/:userRef/:verifyCode - consume an emailed link. Works
// cross-browser with no session at all, and burns the token server-side on
// success (replays get 1025).
export const confirmMfaEmailLink = (userRef, verifyCode) => {
  const path = MFA_ENDPOINTS.emailVerify
    + '/' + encodeURIComponent(decodePathSegment(userRef))
    + '/' + encodeURIComponent(decodePathSegment(verifyCode));

  return extranetApi.get(path)
    .then(unwrapMfaResponse);
};